<template>
  <div>
    <el-badge
        class="item"
        type="warning"
        :max="notificationsLimit"
        :value="unreadNotificationsCount"
        :hidden="!unreadNotificationsCount"
    >
      <span role="button" class="material-icons" @click="open">notifications</span>
    </el-badge>
    <el-drawer :append-to-body="true" :visible.sync="drawerIsOpen" :before-close="handleClose">
      <div slot="title" class="d-flex justify-content-between align-items-center">
        <el-badge
            class="mark"
            :max="notificationsLimit"
            :value="unreadNotificationsCount"
            :hidden="!unreadNotificationsCount"
        >
          <span class="notification_title">{{ $i18n.t('notifications') }} </span>
        </el-badge>
        <el-dropdown trigger="click" v-if="notifications.length">
          <span role="button" class="material-icons">more_vert</span>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item>
              <span role="button" class="notification__clear-all" @click="clear()">
                {{ $i18n.t('clear_all_notification') }}
                <span role="button" class="el-icon-delete"></span>
              </span>
            </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
      </div>
      <div v-if="notifications.length" class="notification__wrapper">
        <div
            role="alert"
            class="el-notification position-relative w-100"
            v-for="(notification, index) in notifications"
            v-loader="loading[notification.id] || clearAll"
            :key="notification.id"
        >
          <i :class="`el-notification__icon el-icon-${getNotificationIcon(notification)}`"></i>
          <div class="el-notification__group is-with-icon">
            <h2 class="el-notification__title" :class="{ unread: !notification.readDate }">
              {{ $i18n.t(notification.title) }}
            </h2>
            <div class="el-notification__content">
              <p v-if="notification.params">{{ getTranslatedNotification(notification) }}</p>
              <p v-else v-html="$i18n.t(notification.msg || 'no_notification_message')"/>
            </div>
            <div class="el-notification__content mt-1">{{ elapsedTime(notification.createDate) }}</div>
          </div>
          <div class="ms-auto d-flex flex-column">
            <div
                class="notification__close el-icon-close"
                @click="closeNoti(index, notification.id)"
                :title="$i18n.t('clear_notification')"
            ></div>
            <div
                v-if="notification.link"
                role="button"
                class="el-icon-arrow-right mt-auto"
                @click="openLink(notification.link)"
                :title="$i18n.t('go_to_notification')"
            ></div>
          </div>
        </div>
      </div>
      <template v-else>
        <el-empty v-if="isReady" class="notification__empty" :description="$i18n.t('not_found_notification')"></el-empty>
      </template>
      <Loader v-if="!isReady"></Loader>
      <div
        v-if="notificationsCount > notifications.length"
        class="el-button el-button--text w-100"
        @click="onLoadNextPage"
      >
        {{ notificationsCount - notifications.length }} {{ $i18n.t('remaining_older_notifications') }}
      </div>
    </el-drawer>
  </div>
</template>

<script>
import moment from 'moment'
import Loader from '../Loader.vue'
import EventBus from '../../utils/event-bus'
import notify from '../../utils/mixin-notify'
import { get, isString, groupBy, isNull } from 'lodash'

const PARAMS_TO_LOCALIZE = [ 'label', 'errorText', 'warningText', 'formName' ]

export default {
  name: 'NotificationCenter',
  components: {
    Loader,
  },
  mixins: [ notify ],
  data() {
    return {
      loading: {},
      isReady: false,
      clearAll: false,
      notifications: [],
      drawerIsOpen: false,
      unreadNotificationsCount: 0,
      notificationsCount: 0,
      notificationsLimit: 0,
      page: 0,
    }
  },
  methods: {
    get,
    openLink(link) {
      location.href = link
    },
    elapsedTime(datetime) {
      return moment(datetime).fromNow()
    },
    open() {
      this.notifications = []
      this.page = 0
      this.loadNotifications()
      this.drawerIsOpen = true
    },
    handleClose(done) {
      this.unreadNotificationsCount = 0
      done()
    },
    async showMessages(messages = []) {
      const unreadMessages = messages?.filter((message) => isNull(message.readDate))
      const messagesByType = groupBy(unreadMessages, 'type')
      for (const type of Object.keys(messagesByType)) {
        const messagesByTitle = groupBy(messagesByType[type], 'title')
        for (const title of Object.keys(messagesByTitle)) {
          const messages = messagesByTitle[title]
          const count = messages.length
          const message = Object.assign(this.transformServerMessage(messages[0]), { save: false, count })
          await this.showNotification(message)
        }
      }
    },
    async getServerMessages() {
      this.page = 0
      const { body: messages } = await this.readNotifications(false)

      await this.showMessages(messages)

      this.unreadNotificationsCount = 0
      this.isReady = true
    },
    readNotifications(read = true) {
      return this.$client.execute({
        operationId: 'Notification_readNotifications',
        parameters: { read },
      })
    },
    async onLoadNextPage() {
      this.page++
      await this.loadNotifications()
    },
    async loadNotifications() {
      this.isReady = false
      const { body } = await this.$client.execute({
        operationId: 'Notification_loadNotifications',
        parameters: {
          page: this.page,
        },
      })
      const { notifications, total } = body
      if (+total > 0) {
        this.notifications = this.notifications.concat(notifications)
        this.notificationsCount = total
      }
      this.isReady = true
    },
    closeNoti(idx, id) {
      this.$set(this.loading, id, true)
      this.notifications[idx].storno = 1
      this.clear(id)
    },
    clear(id) {
      this.notifications = []
      this.page = 0
      const params = {}

      if (id) {
        params.id = id
      } else {
        this.clearAll = true
        this.notificationsCount = 0
      }

      this.$client
        .execute({
          operationId: 'Notification_clearNotification',
          parameters: params,
          requestContentType: 'application/x-www-form-urlencoded',
        })
        .then(() => {
          if (id) {
            delete this.loading[id]
          } else {
            this.clearAll = false
          }
          this.loadNotifications()
        })
    },
    getNotificationIcon(noti) {
      const type = get(noti, 'config.type', '')
      return type && isString(type) ? type.toLowerCase() : ''
    },
    getTranslatedNotification({ msg, params }) {
      Object.keys(params).forEach((key) => {
        if (PARAMS_TO_LOCALIZE.includes(key)) {
          params[key] = this.$i18n.t(params[key])
        }
      })
      return this.$i18n.interpolate(this.$i18n.t(msg), params)
    },
  },
  mounted() {
    this.notificationsLimit = this.$CORE.notificationLimit

    EventBus.$on('refresh-notifications', () => {
      this.notifications = []
      this.page = 0
      this.loadNotifications()
    })

    EventBus.$on('show-notifications', () => {
      this.getServerMessages()
    })

    //  kicsit késleltetve jelenítjük meg a notikat
    setTimeout( () => this.getServerMessages(), this.$CORE.notificationDelay)
  },
  beforeDestroy() {
    EventBus.$off('refresh-notifications')
    EventBus.$off('show-notifications')
  },
}
</script>

<style lang="scss" scoped>
.notification__clear-all {
  color: $drawer-header;

  span {
    font-size: 18px;
  }
}

.notification_title {
  padding-right: 10px;
}

.notification__wrapper {
  padding: 3px;

  .notification__close {
    cursor: pointer;
  }

  .notification__time {
    margin-top: 5px;
  }
}

.mark {
  background-color: transparent;
}

.item {
  color: $color-text-secondary;
}
</style>
<style>
.notification__cursor-pointer {
  cursor: pointer;
}
</style>
